<template>
    <div class="widget-create-object">
        <b-dropdown id="dropdown btn-home-widget-new-object" variant="primary" right no-caret>
            <template #button-content>
                <div class="btn-title">
                    <i class="fa fa-plus" />
                    {{ $t('new') }}
                </div>
            </template>
            <b-dropdown-item
                id="btn-home-widget-new-objective"
                v-if="canViewOKR"
                @click="openNewObjectiveModal"
                :disabled="!userCanCreateOkr"
                ><img src="@/assets/icons/okr_grey.svg" />{{ $t('objectives.objective') }}</b-dropdown-item
            >
            <b-dropdown-item
                id="btn-home-widget-new-pdi"
                v-if="canViewPDI"
                @click="openNewPdiModal"
                :disabled="!userCanCreatePdi"
                ><img src="@/assets/icons/pdi_grey.svg" />{{ $t('pdis.pdi') }}</b-dropdown-item
            >
            <b-dropdown-item
                id="btn-home-widget-new-initiative"
                v-if="canViewInitiatives"
                @click="openNewInitiativeModal"
                :disabled="!userCanCreateOkr"
                ><img src="@/assets/icons/initiative_grey.svg" />{{ $t('daily.initiative') }}</b-dropdown-item
            >
        </b-dropdown>
        <new-objective-modal @onCreatedObjective="$emit('objectiveCreated')" />
        <new-pdi-modal :userId="user.id" @pdiCreated="$emit('pdiCreated')" />
        <new-initiative-modal @created="$store.dispatch('getOKRsFromAPI')" />
    </div>
</template>

<script>
import NewObjectiveModal from '@/views/okr/objective/new'
import { New as NewPdiModal } from '@/views/pdi'
import { NewInitiativeModal } from '@/views/okr/initiatives'
import { userPermissionsMixin } from '@/mixins'
import api from '@/services/api'
import { includes } from 'lodash'

export default {
    components: {
        NewObjectiveModal,
        NewPdiModal,
        NewInitiativeModal,
    },
    mixins: [userPermissionsMixin],
    props: {
        user: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            superUserRoles: ['god', 'administrator', 'local_administrator'],
            periodsInProgress: [],
        }
    },
    computed: {
        currentUser() {
            return this.$store.state.currentUser.user
        },
        userIsCurrentUser() {
            return this.user.id == this.currentUser.id
        },
        anyPeriodInProgress() {
            return this.periodsInProgress.length > 0
        },
        canViewPDI() {
            const userHasPDI = this.isUserHasModule('pdi')
            const currentUserHasPDI = this.isCurrentUserHasModule('pdi')

            if (this.userIsCurrentUser) {
                return currentUserHasPDI && this.$route.params.id
            }
            return userHasPDI && currentUserHasPDI && this.$route.params.id
        },
        canViewOKR() {
            return this.isUserHasModule('okr') && this.isCurrentUserHasModule('okr')
        },
        canViewInitiatives() {
            const userHasDaily = this.isUserHasModule('daily')
            const currentUserHasDaily = this.isCurrentUserHasModule('daily')

            if (this.userIsCurrentUser) {
                return currentUserHasDaily
            }

            return userHasDaily && currentUserHasDaily && this.canViewOKR
        },
        userCanCreateOkr() {
            const availableRoles = [...this.superUserRoles, 'is_okr_leader']

            return this.currentUserHasPermission(availableRoles) && this.anyPeriodInProgress
        },
        userCanCreatePdi() {
            const availableRoles = [...this.superUserRoles, 'is_values_master']

            return (
                (this.currentUserHasPermission(availableRoles) || this.isCommitteeEvaluator()) &&
                this.anyPeriodInProgress
            )
        },
    },
    mounted() {
        this.getInProgressPeriods()
    },
    methods: {
        isCurrentUserHasModule(module) {
            return includes(this.currentUser.has_modules, `has_${module}`)
        },
        isUserHasModule(module) {
            return includes(this.user.has_modules, `has_${module}`)
        },
        currentUserHasPermission(availableRoles) {
            return (
                this.userIsCurrentUser ||
                this.isManagerOfUser(this.currentUser, this.user) ||
                this.userHasPermission(this.currentUser, availableRoles, [])
            )
        },
        isCommitteeEvaluator() {
            return this.isCommitteeMember(this.currentUser.evaluator_committees, this.user.rated_committees)
        },
        getInProgressPeriods() {
            api.get('/periods', {
                params: {
                    sortedBy: 'desc',
                    orderBy: 'end_date',
                    scopes: 'notArchived',
                },
            })
                .then((resp) => {
                    const {
                        data: { data },
                    } = resp

                    const periods = data.filter(
                        (period) => period.status === 'in_progress' || period.status === 'in_draft'
                    )

                    this.periodsInProgress = periods
                })
                .catch((e) => {
                    console.log(e)
                })
        },
        openNewObjectiveModal() {
            this.$bvModal.show('new-objective-modal')
        },
        openNewPdiModal() {
            this.$bvModal.show('new-pdi-modal')
        },
        openNewInitiativeModal() {
            this.$bvModal.show('new-initiative-modal')
        },
    },
}
</script>

<style lang="scss" scoped>
.widget-create-object {
    width: max-content;
}
::v-deep .btn-primary.dropdown-toggle,
::v-deep .dropdown.show .btn-primary {
    background-color: #009ada;
    border-color: #009ada;
    color: white;
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 700;
    font-size: 1rem;
    justify-content: center;
    align-items: center;
    padding: 0.65rem 0.8rem;
    border-radius: 16px;
    .fa-plus {
        margin-right: 0.4rem;
    }
}

::v-deep .dropdown-menu {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    box-sizing: border-box;
    box-shadow: -1px -2px 5px #bec0c0, 1px 2px 5px #bec0c0;
    border-radius: 5px;
    .dropdown-item {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.7rem;
        padding: 0.8rem 0.7rem;
        img {
            width: 1.3rem;
        }
        font-family: 'Roboto';
        font-style: normal;
        font-weight: 700;
        font-size: 0.85rem;
        line-height: 14px;
        display: flex;
        align-items: center;
        color: #6e6e6e;
        img {
            filter: invert(0.6);
        }
    }
    .dropdown-item.disabled {
        img {
            filter: none;
        }
        color: #bec0c0;
    }
}
</style>
